import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';
import {
  getDefaultFutureDate,
  getDaysInMonth,
  parseYYYYMMDD,
  toYYYYMMDD
} from '../../utils/datePickerUtils';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface DatePickerFieldProps {
  variableName: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Date picker: three spinners (year, month, day).
 * Outputs "YYYY-MM-DD" to the variable.
 */
const DatePickerField: React.FC<DatePickerFieldProps> = ({
  variableName,
  className,
  ariaLabel = 'Select date'
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const uiStore = useUIStore();

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: 5 }, (_, i) => currentYear + i),
    [currentYear]
  );

  const initialState = useMemo(() => {
    const parsed = parseYYYYMMDD(storedValue);
    if (parsed) {
      return parsed;
    }
    return getDefaultFutureDate();
  }, []);

  const [year, setYear] = useState(initialState.year);
  const [month, setMonth] = useState(initialState.month);
  const [day, setDay] = useState(initialState.day);

  const clampedDay = useMemo(() => {
    const max = getDaysInMonth(year, month);
    return Math.min(Math.max(1, day), max);
  }, [year, month, day]);

  useEffect(() => {
    if (clampedDay !== day) {
      setDay(clampedDay);
    }
  }, [clampedDay, day]);

  // Sync from stored value when it changes (e.g. broadcast from another client)
  useEffect(() => {
    const parsed = parseYYYYMMDD(storedValue);
    if (parsed) {
      setYear(parsed.year);
      setMonth(parsed.month);
      setDay(parsed.day);
      setError(null);
    }
  }, [storedValue]);

  const submitValue = useCallback(
    async (yyyymmdd: string) => {
      if (yyyymmdd === (storedValue ?? '').toString().trim()) return;

      try {
        setIsSaving(true);
        setError(null);
        await submitUserInput(variableName, yyyymmdd);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update date';
        setError(message);
        uiStore.setError({
          code: 'DATE_PICKER_ERROR',
          message
        });
      } finally {
        setIsSaving(false);
      }
    },
    [variableName, storedValue, submitUserInput, uiStore]
  );

  const updateAndSubmit = useCallback(
    (newYear: number, newMonth: number, newDay: number) => {
      const maxDay = getDaysInMonth(newYear, newMonth);
      const clamped = Math.min(Math.max(1, newDay), maxDay);
      const yyyymmdd = toYYYYMMDD(newYear, newMonth, clamped);
      void submitValue(yyyymmdd);
    },
    [submitValue]
  );

  const cycleYearUp = useCallback(() => {
    const idx = years.indexOf(year);
    const nextIdx = idx >= years.length - 1 ? 0 : idx + 1;
    const nextYear = years[nextIdx] ?? year;
    setYear(nextYear);
    updateAndSubmit(nextYear, month, day);
  }, [year, month, day, years, updateAndSubmit]);

  const cycleYearDown = useCallback(() => {
    const idx = years.indexOf(year);
    const nextIdx = idx <= 0 ? years.length - 1 : idx - 1;
    const nextYear = years[nextIdx] ?? year;
    setYear(nextYear);
    updateAndSubmit(nextYear, month, day);
  }, [year, month, day, years, updateAndSubmit]);

  const cycleMonthUp = useCallback(() => {
    const next = month >= 12 ? 1 : month + 1;
    const nextYear = month >= 12 ? year + 1 : year;
    setMonth(next);
    if (month >= 12) setYear(nextYear);
    updateAndSubmit(month >= 12 ? nextYear : year, next, day);
  }, [year, month, day, updateAndSubmit]);

  const cycleMonthDown = useCallback(() => {
    const next = month <= 1 ? 12 : month - 1;
    const nextYear = month <= 1 ? year - 1 : year;
    setMonth(next);
    if (month <= 1) setYear(nextYear);
    updateAndSubmit(month <= 1 ? nextYear : year, next, day);
  }, [year, month, day, updateAndSubmit]);

  const cycleDayUp = useCallback(() => {
    const max = getDaysInMonth(year, month);
    const next = day >= max ? 1 : day + 1;
    setDay(next);
    updateAndSubmit(year, month, next);
  }, [year, month, day, updateAndSubmit]);

  const cycleDayDown = useCallback(() => {
    const max = getDaysInMonth(year, month);
    const next = day <= 1 ? max : day - 1;
    setDay(next);
    updateAndSubmit(year, month, next);
  }, [year, month, day, updateAndSubmit]);

  // Initial submit: if we started with a default and store was empty, persist it
  const hasInitialized = React.useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const parsed = parseYYYYMMDD(storedValue);
    if (!parsed && (storedValue ?? '').toString().trim() === '') {
      const yyyymmdd = toYYYYMMDD(initialState.year, initialState.month, initialState.day);
      void submitValue(yyyymmdd);
    }
  }, [storedValue, initialState, submitValue]);

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-date-picker'];
    if (className) classes.push(className);
    if (error) classes.push('rich-text-date-picker--error');
    return classes.join(' ');
  }, [className, error]);

  return (
    <div
      className={rootClassName}
      data-variable={variableName}
      data-testid={`date-picker-${variableName}`}
      role="group"
      aria-label={ariaLabel}
    >
      <div className="rich-text-date-picker__row">
        {/* Day spinner */}
        <div className="rich-text-date-picker__spinner">
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
            onClick={cycleDayUp}
            disabled={isSaving}
            aria-label="Previous day"
          >
            ▲
          </button>
          <span className="rich-text-spinner__value">
            {String(clampedDay).padStart(2, '0')}
          </span>
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
            onClick={cycleDayDown}
            disabled={isSaving}
            aria-label="Next day"
          >
            ▼
          </button>
        </div>

        {/* Month spinner */}
        <div className="rich-text-date-picker__spinner">
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
            onClick={cycleMonthUp}
            disabled={isSaving}
            aria-label="Previous month"
          >
            ▲
          </button>
          <span className="rich-text-spinner__value">
            {MONTH_NAMES[month - 1]}
          </span>
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
            onClick={cycleMonthDown}
            disabled={isSaving}
            aria-label="Next month"
          >
            ▼
          </button>
        </div>

        {/* Year spinner */}
        <div className="rich-text-date-picker__spinner">
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
            onClick={cycleYearUp}
            disabled={isSaving}
            aria-label="Previous year"
          >
            ▲
          </button>
          <span className="rich-text-spinner__value">{year}</span>
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
            onClick={cycleYearDown}
            disabled={isSaving}
            aria-label="Next year"
          >
            ▼
          </button>
        </div>
      </div>

      {error && (
        <span
          className="rich-text-date-picker__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`date-picker-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default DatePickerField;
